import React from 'react'
import { Box, Typography } from '@mui/material'
import { PrescriptionData } from '@/shared/types/yakurekiPrescription'

type Props = {
  currentPrescription?: PrescriptionData
  previousPrescription?: PrescriptionData
  currentInstitution?: string
  previousInstitution?: string
}

const PrescriptionInfo: React.FC<Props> = ({
  currentPrescription,
  previousPrescription,
  currentInstitution,
  previousInstitution,
}) => {
  // 日付フォーマット関数
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return ''

    try {
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()
      return `${year}/${month}/${day}`
    } catch {
      return ''
    }
  }

  // log/33 design/03: Notion 流に Paper border + blue Rp ラベルを撤去. 余白と typography で構成.
  const renderPrescriptionGroup = (
    group: PrescriptionData['prescription_groups'][0],
    index: number,
    isLast = false
  ) => (
    <Box sx={{ mb: isLast ? 0 : 1.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          display: 'block',
          mb: 0.5,
        }}
      >
        Rp.{index + 1}
      </Typography>
      {group.drugs.map((drug, drugIndex) => (
        <Typography
          key={drugIndex}
          variant="body2"
          sx={{ lineHeight: 1.5, mb: 0.25, pl: 1.5 }}
        >
          {drug.drug_name}
          <Box component="span" sx={{ mx: 1.5 }} />
          {drug.quantity}
          {drug.unit}
        </Typography>
      ))}
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.5, mt: 0.25, pl: 1.5, color: 'text.secondary' }}
      >
        {group.usage.name}
        <Box component="span" sx={{ mx: 1.5 }} />
        {group.days}日分
      </Typography>
    </Box>
  )

  const renderPrescription = (
    prescriptionData: PrescriptionData,
    title: string,
    institution?: string
  ) => {
    const formattedDate = formatDate(prescriptionData.prescription_date)
    const displayTitle = formattedDate ? `${title}（${formattedDate}）` : title

    return (
      <Box>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}
        >
          {displayTitle}
        </Typography>
        {institution && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {institution}
          </Typography>
        )}
        <Box>
          {prescriptionData.prescription_groups.map((group, index) => (
            <Box key={index}>
              {renderPrescriptionGroup(
                group,
                index,
                index === prescriptionData.prescription_groups.length - 1
              )}
            </Box>
          ))}
        </Box>
      </Box>
    )
  }

  // 処方データがある場合の表示
  if (currentPrescription || previousPrescription) {
    return (
      <Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 3,
          }}
        >
          {currentPrescription && (
            <Box>
              {renderPrescription(
                currentPrescription,
                '今回処方',
                currentInstitution
              )}
            </Box>
          )}
          {previousPrescription ? (
            <Box>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}
              >
                前回処方
                {previousPrescription.prescription_date &&
                  `（${formatDate(previousPrescription.prescription_date)}）`}
              </Typography>
              {previousInstitution && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  {previousInstitution}
                </Typography>
              )}
              <Box>
                {previousPrescription.prescription_groups.map(
                  (group, index) => (
                    <Box key={index}>
                      {renderPrescriptionGroup(
                        group,
                        index,
                        index ===
                          previousPrescription.prescription_groups.length - 1
                      )}
                    </Box>
                  )
                )}
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}
              >
                前回処方
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: 'block', py: 1 }}
              >
                前回処方はありません
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  // 処方データがない場合
  return null
}

export default PrescriptionInfo
